import { api, affiliateApi, brandApi } from '../lib/axios';

export interface PaymentMethodField {
  id: string;
  paymentMethodId: string;
  fieldName: string;
  label: string;
  isRequired: boolean;
  createdAt: string;
  updatedAt: string;
  placeHolder?: string;
}

export interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  fields: PaymentMethodField[];
}

export interface BrandSupportedPaymentMethod {
  id: string;
  brandDetailId: string;
  paymentMethodId: string;
  createdAt: string;
  updatedAt: string;
  paymentMethod: PaymentMethod;
}

export interface AffiliatePaymentMethod {
  id: string;
  affiliateId: string;
  paymentMethodId: string;
  fieldData: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  paymentMethod: PaymentMethod;
}

export interface SetupPaymentMethodPayload {
  paymentMethodId: string;
  fieldValues: Array<{
    fieldName: string;
    value: string;
  }>;
}

export const paymentMethodsService = {
  /**
   * Fetches all available payment methods from the API
   * @returns Promise with array of payment methods
   */
  async getAllPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await api.get('/payment-method');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  },

  /**
   * Updates brand's preferred payment methods
   * @param paymentMethodIds Array of payment method IDs
   * @returns Promise with the updated brand data
   */
  async setBrandPaymentMethods(paymentMethodIds: string[]): Promise<any> {
    try {
      const response = await brandApi.post('/payment-method/setup-supported', { paymentMethodIds });
      return response.data;
    } catch (error) {
      console.error('Error updating brand payment methods:', error);
      throw error;
    }
  },
  /**
   * Updates program's preferred payment methods
   * @param paymentMethodIds Array of payment method IDs
   * @returns Promise with the updated program data
   */
  async updateBrandPaymentMethods(paymentMethodIds: string[]): Promise<any> {
    try {
      const response = await brandApi.post('/payment-method/bulk-update-brand', { paymentMethodIds });
      return response.data;
    } catch (error) {
      console.error('Error updating brand payment methods:', error);
      throw error;
    }
  },

  /**
   * Fetches supported payment methods for a specific brand
   * @param brandId The brand ID to fetch supported payment methods for
   * @returns Promise with array of supported payment methods
   */
  async getBrandSupportedPaymentMethods(brandId: string): Promise<BrandSupportedPaymentMethod[]> {
    try {
      const response = await api.get(`/payment-method/brand/${brandId}/supported`);
      return response.data;
    } catch (error) {
      console.error('Error fetching brand supported payment methods:', error);
      throw error;
    }
  },

  /**
   * Fetches supported payment methods for a specific program
   * @param programId The program ID to fetch supported payment methods for
   * @returns Promise with array of supported payment methods
   */
  async getProgramSupportedPaymentMethods(programId: string): Promise<BrandSupportedPaymentMethod[]> {
    try {
      const response = await api.get(`/payment-method/program/${programId}/supported`);
      return response.data;
    } catch (error) {
      console.error('Error fetching program supported payment methods:', error);
      throw error;
    }
  },

  /**
   * Fetches payment information for a specific affiliate
   * @param affiliateId The affiliate ID to fetch payment info for
   * @returns Promise with array of affiliate payment methods
   */
  async getAffiliatePaymentInfo(affiliateId: string): Promise<AffiliatePaymentMethod[]> {
    try {
      const response = await affiliateApi.get(`/payment-method/affiliate/${affiliateId}/payment-info`);
      return response.data;
    } catch (error) {
      console.error('Error fetching affiliate payment info:', error);
      throw error;
    }
  },
  
  /**
   * Sets up a payment method for an affiliate
   * @param payload The payment method setup data
   * @returns Promise with the created payment method
   */
  async setupPaymentMethod(payload: SetupPaymentMethodPayload): Promise<AffiliatePaymentMethod> {
    try {
      const response = await affiliateApi.post('/payment-method/affiliate/setup-payment', payload);
      return response.data;
    } catch (error) {
      console.error('Error setting up payment method:', error);
      throw error;
    }
  },

  /**
   * Bulk updates payment methods for an affiliate
   * @param paymentMethods Array of payment methods with their field values
   * @returns Promise with the updated payment methods
   */
  async bulkUpdatePaymentMethod(paymentMethods: {
    paymentMethodId: string;
    fieldValues: { fieldName: string; value: string }[];
  }[]): Promise<AffiliatePaymentMethod[]> {
    try {
      const payload = { paymentMethods };
      const response = await affiliateApi.post('/payment-method/affiliate/bulk-update-payment', payload);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating payment method:', error);
      throw error;
    }
  },

  /**
   * One answer to "can I be paid right now, and by which brands?" — feeds the
   * dashboard banner, the payout page notice and the inline add-method form.
   */
  async getPayoutReadiness(): Promise<PayoutReadiness> {
    const response = await affiliateApi.get('/payout/affiliate/readiness');
    return response.data;
  },
};

export interface PayoutReadinessProgram {
  programId: string;
  brandDetailId: string;
  brandName: string;
  available: number;
  supportedPaymentMethods: Array<{ id: string; name: string }>;
  hasMatchingMethod: boolean;
}

export interface PayoutReadiness {
  hasAnyMethod: boolean;
  methods: Array<{ id: string; name: string }>;
  programs: PayoutReadinessProgram[];
  /** Programs where money is waiting and no saved method can receive it. */
  needsAttention: PayoutReadinessProgram[];
}

/**
 * Shape of the typed 400 the payout-request endpoints return when the caller
 * has no usable payout method. The client opens an inline form filtered to
 * `supportedPaymentMethods` (empty = any method will do) and retries.
 */
export interface PayoutMethodRequiredError {
  code: 'PAYOUT_METHOD_REQUIRED';
  message: string;
  brandName?: string;
  supportedPaymentMethods?: Array<{ id: string; name: string }>;
}

export function isPayoutMethodRequiredError(
  data: unknown,
): data is PayoutMethodRequiredError {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as { code?: string }).code === 'PAYOUT_METHOD_REQUIRED'
  );
}
